import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { type ApplicationConfig, importProvidersFrom, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
	browserLocalPersistence,
	browserPopupRedirectResolver,
	browserSessionPersistence,
	getAuth,
	indexedDBLocalPersistence,
	initializeAuth,
	provideAuth
} from '@angular/fire/auth';
import { FormsModule } from '@angular/forms';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
	provideRouter,
	RouteReuseStrategy,
	withComponentInputBinding,
	withInMemoryScrolling,
	withPreloading
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { appRoutes } from '@sp-client/app/app.routes';
import { firebaseAuthInterceptor } from '@sp-client/app/interceptors/firebase-auth.interceptor';
import { environment } from '@sp-client/environments/environment';
import { QuicklinkStrategy } from 'ngx-quicklink';
import { provideNgxStripe } from 'ngx-stripe';
import { authErrorInterceptor } from './interceptors/auth-error.interceptor';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { CustomRouteReuseStrategy } from './route-reuse-strategy';

export const appConfig: ApplicationConfig = {
	providers: [
		provideRouter(
			appRoutes,
			withPreloading(QuicklinkStrategy),
			withComponentInputBinding(),
			withInMemoryScrolling({
				scrollPositionRestoration: 'disabled'
			})
		),
		provideFirebaseApp(() => initializeApp(environment.firebase)),
		provideAuth(() => {
			if (typeof window === 'undefined') {
				return getAuth();
			}
			try {
				return initializeAuth(getApp(), {
					popupRedirectResolver: browserPopupRedirectResolver,
					persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence]
				});
			} catch {
				// Auth may already be initialized during hydration; reuse existing instance.
				return getAuth();
			}
		}),
		provideClientHydration(),
		provideZoneChangeDetection({ eventCoalescing: true }),
		importProvidersFrom(FormsModule),
		provideHttpClient(
			withInterceptors([credentialsInterceptor, firebaseAuthInterceptor, authErrorInterceptor]),
			withFetch()
		),
		provideNgxStripe(environment.stripePublishableKey, {
			developerTools: {
				assistant: {
					enabled: environment.stripeDeveloperAssistantEnabled
				}
			}
		}),
		provideAnimationsAsync(),
		provideServiceWorker('ngsw-worker.js', {
			enabled: !isDevMode() && environment.production,
			registrationStrategy: 'registerWhenStable:30000'
		}),
		{ provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }
	]
};
